'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Lock, Mail, Eye, EyeOff, ShieldCheck, ArrowRight, Heart, UserPlus, Phone, MessageSquare } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

type Mode = 'login' | 'signup';
type AuthMethod = 'email' | 'phone';
type OtpStep = 'phone' | 'verify';

export default function PatientLoginPage() {
  const router = useRouter();
  const { signIn, signUp } = useAuth();

  // Email/password state
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Auth method toggle
  const [authMethod, setAuthMethod] = useState<AuthMethod>('email');

  // Phone OTP state
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpStep, setOtpStep] = useState<OtpStep>('phone');

  // Shared state
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const supabase = createClient();

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (mode === 'login') {
        await signIn(email.trim(), password);
        router.push('/patient-dashboard');
      } else {
        if (!fullName.trim()) {
          setError('Please enter your full name.');
          setLoading(false);
          return;
        }
        await signUp(email.trim(), password, { fullName: fullName.trim() });
        router.push('/patient-dashboard');
      }
    } catch (err: any) {
      const msg = err?.message || 'Something went wrong. Please try again.';
      if (msg.includes('Invalid login credentials')) {
        setError('Invalid email or password. Please check your credentials.');
      } else if (msg.includes('User already registered')) {
        setError('An account with this email already exists. Please sign in.');
      } else if (msg.includes('Password should be')) {
        setError('Password must be at least 6 characters.');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');

    const cleaned = phone.trim().replace(/\s+/g, '');
    if (!cleaned.startsWith('+')) {
      setError('Please enter phone number with country code (e.g. +91XXXXXXXXXX).');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({ phone: cleaned });
      if (error) throw error;
      setSuccessMsg(`OTP sent to ${cleaned}. Please check your messages.`);
      setOtpStep('verify');
    } catch (err: any) {
      setError(err?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const cleaned = phone.trim().replace(/\s+/g, '');
    try {
      const { error } = await supabase.auth.verifyOtp({
        phone: cleaned,
        token: otp.trim(),
        type: 'sms',
      });
      if (error) throw error;
      router.push('/patient-dashboard');
    } catch (err: any) {
      setError(err?.message || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetOtpFlow = () => {
    setOtpStep('phone');
    setOtp('');
    setError('');
    setSuccessMsg('');
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel — brand */}
      <div className="hidden lg:flex lg:w-1/2 hero-gradient flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-64 h-64 rounded-full bg-white blur-3xl" />
          <div className="absolute bottom-20 right-10 w-80 h-80 rounded-full bg-sky-300 blur-3xl" />
        </div>
        <div className="relative z-10">
          <Link href="/" className="flex items-center gap-3">
            <AppLogo size={56} />
            <div>
              <div className="text-white font-extrabold text-2xl leading-tight">51 Plus Lab</div>
              <div className="text-sky-200 text-xs font-semibold uppercase tracking-wide">NABL Accredited Diagnostics</div>
            </div>
          </Link>
        </div>
        <div className="relative z-10 space-y-8">
          <div>
            <h1 className="text-white text-4xl font-bold leading-tight mb-4">
              Your Health,<br />Your Records
            </h1>
            <p className="text-sky-100 text-lg leading-relaxed">
              Access your complete health history — past appointments, lab reports, health summaries, and account settings — all in one secure place.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { label: 'Appointment History', desc: 'View all past visits' },
              { label: 'Lab Reports', desc: 'Download test results' },
              { label: 'Saved Packages', desc: 'Your favourite packages' },
              { label: 'Price Alerts', desc: 'Get notified on discounts' },
            ].map((f) => (
              <div key={f.label} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <div className="text-white font-semibold text-sm">{f.label}</div>
                <div className="text-sky-200 text-xs mt-0.5">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 flex items-center gap-2 text-sky-200 text-sm">
          <ShieldCheck size={16} />
          <span>Your health data is encrypted and private</span>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <AppLogo size={48} />
            <div>
              <div className="font-extrabold text-xl text-foreground">51 Plus Lab</div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Patient Portal</div>
            </div>
          </div>

          <div className="mb-6">
            <div className="inline-flex items-center gap-2 bg-secondary text-primary text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
              <Heart size={13} />
              Patient Portal
            </div>
            <h2 className="text-3xl font-bold text-foreground mb-2">
              {authMethod === 'email' ? (mode === 'login' ? 'Welcome back' : 'Create account') : 'Mobile OTP Login'}
            </h2>
            <p className="text-muted-foreground">
              {authMethod === 'email'
                ? (mode === 'login' ? 'Sign in to access your health records' : 'Sign up to save favourites, alerts & bookings')
                : 'Sign in securely using your mobile number'}
            </p>
          </div>

          {/* Auth method toggle */}
          <div className="flex bg-muted rounded-xl p-1 mb-6">
            <button
              type="button"
              onClick={() => { setAuthMethod('email'); setError(''); setSuccessMsg(''); }}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition-all ${
                authMethod === 'email' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Mail size={14} /> Email
            </button>
            <button
              type="button"
              onClick={() => { setAuthMethod('phone'); setError(''); setSuccessMsg(''); resetOtpFlow(); }}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition-all ${
                authMethod === 'phone' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Phone size={14} /> Mobile OTP
            </button>
          </div>

          {/* EMAIL / PASSWORD SECTION */}
          {authMethod === 'email' && (
            <>
              {/* Sign In / Sign Up sub-toggle */}
              <div className="flex bg-muted rounded-xl p-1 mb-6">
                <button
                  type="button"
                  onClick={() => { setMode('login'); setError(''); }}
                  className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${
                    mode === 'login' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Sign In
                </button>
                <button
                  type="button"
                  onClick={() => { setMode('signup'); setError(''); }}
                  className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${
                    mode === 'signup' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Sign Up
                </button>
              </div>

              <form onSubmit={handleEmailSubmit} className="space-y-5">
                {mode === 'signup' && (
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-1.5">Full Name</label>
                    <div className="relative">
                      <UserPlus size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        placeholder="Your full name"
                        required
                        className="input-field pl-10"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">Email Address</label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      required
                      className="input-field pl-10"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">Password</label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={mode === 'signup' ? 'Min. 6 characters' : 'Enter your password'}
                      required
                      className="input-field pl-10 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="bg-danger/10 border border-danger/20 text-danger text-sm rounded-lg px-4 py-3">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full justify-center text-base py-3"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                      {mode === 'login' ? 'Signing in…' : 'Creating account…'}
                    </span>
                  ) : (
                    <>
                      {mode === 'login' ? 'Sign In' : 'Create Account'}
                      <ArrowRight size={16} />
                    </>
                  )}
                </button>
              </form>

              <div className="mt-6 p-4 bg-muted rounded-xl border border-border">
                <p className="text-xs font-semibold text-muted-foreground mb-2">Demo credentials:</p>
                <p className="text-xs text-foreground font-mono">patient@demo.com / patient123</p>
              </div>
            </>
          )}

          {/* PHONE OTP SECTION */}
          {authMethod === 'phone' && (
            <>
              {otpStep === 'phone' && (
                <form onSubmit={handleSendOtp} className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-1.5">Mobile Number</label>
                    <div className="relative">
                      <Phone size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+91 98765 43210"
                        required
                        className="input-field pl-10"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1.5">Include country code (e.g. +91 for India)</p>
                  </div>

                  {error && (
                    <div className="bg-danger/10 border border-danger/20 text-danger text-sm rounded-lg px-4 py-3">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full justify-center text-base py-3"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                        </svg>
                        Sending OTP…
                      </span>
                    ) : (
                      <>
                        <MessageSquare size={16} />
                        Send OTP
                      </>
                    )}
                  </button>
                </form>
              )}

              {otpStep === 'verify' && (
                <form onSubmit={handleVerifyOtp} className="space-y-5">
                  {successMsg && (
                    <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg px-4 py-3">
                      {successMsg}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-1.5">Enter OTP</label>
                    <div className="relative">
                      <MessageSquare size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        type="text"
                        inputMode="numeric"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="6-digit OTP"
                        required
                        maxLength={6}
                        className="input-field pl-10 tracking-widest text-center text-lg font-semibold"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1.5">OTP sent to {phone}</p>
                  </div>

                  {error && (
                    <div className="bg-danger/10 border border-danger/20 text-danger text-sm rounded-lg px-4 py-3">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full justify-center text-base py-3"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                        </svg>
                        Verifying…
                      </span>
                    ) : (
                      <>
                        Verify & Sign In
                        <ArrowRight size={16} />
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={resetOtpFlow}
                    className="w-full text-sm text-muted-foreground hover:text-primary transition-colors text-center py-1"
                  >
                    ← Change number / Resend OTP
                  </button>
                </form>
              )}

              <div className="mt-6 p-4 bg-muted rounded-xl border border-border">
                <p className="text-xs font-semibold text-muted-foreground mb-1">Note:</p>
                <p className="text-xs text-muted-foreground">SMS delivery requires Twilio to be configured in Supabase → Authentication → Phone settings.</p>
              </div>
            </>
          )}

          <div className="mt-6 text-center">
            <Link href="/" className="text-sm text-muted-foreground hover:text-primary transition-colors">
              ← Back to main site
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
